import React, { useState } from "react";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    Snackbar,
    TextField,
    Typography,
} from "@mui/material";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import axios from "axios";
import {
    SCHEDULERX_HEALTH_ACTION_ITEMS_URL,
    SCHEDULERX_SEND_MEETING_EMAIL_URL,
} from "../config/api";

const HealthActionItems = ({ transcription }) => {
    const [meetingData, setMeetingData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statusMap, setStatusMap] = useState({});
    const [editingIndex, setEditingIndex] = useState(null);
    const [editData, setEditData] = useState({});
    const [openDialog, setOpenDialog] = useState(false);
    const [organizerEmail, setOrganizerEmail] = useState("");
    const [ccEmail, setCcEmail] = useState("");
    const [bccEmail, setBccEmail] = useState("");
    const [errors, setErrors] = useState({});
    const [sendingMail, setSendingMail] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarSeverity, setSnackbarSeverity] = useState("success");

    const saveEmailSetup = () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const errs = {};
        if (!organizerEmail.trim()) {
            errs.organizerEmail = "Required";
        } else if (!emailRegex.test(organizerEmail.trim())) {
            errs.organizerEmail = "Invalid email format";
        }
        setErrors(errs);
        if (Object.keys(errs).length > 0) return;

        sessionStorage.setItem(
            "meeting_email_info",
            JSON.stringify({ organizerEmail, ccEmail, bccEmail })
        );
        setOpenDialog(false);
        setSnackbarMessage("Email recipients saved.");
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
    };

    const generateActionItems = async () => {
        if (!transcription.trim()) {
            setSnackbarMessage("Add a transcription before generating action items.");
            setSnackbarSeverity("warning");
            setSnackbarOpen(true);
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(
                SCHEDULERX_HEALTH_ACTION_ITEMS_URL,
                { transcription },
                { headers: { "Content-Type": "application/json" } }
            );
            const parsed = { ...data };
            if (parsed.action_items) {
                parsed.action_items = parsed.action_items.map((item) => ({
                    task: item.task || "",
                    assigned_to: item.assigned_to || item.assignee || "",
                    deadline: item.deadline || item.due_date || "",
                    priority: item.priority || "",
                }));
            }
            setMeetingData(parsed);
            setStatusMap({});
            setEditingIndex(null);
        } catch (error) {
            setSnackbarMessage(error.response?.data?.detail || "Failed to generate action items.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setLoading(false);
        }
    };

    const handleSendMail = async () => {
        const approvedItems = meetingData.action_items.filter(
            (_, index) => statusMap[index] === "approved"
        );
        if (approvedItems.length === 0) {
            setSnackbarMessage("No approved action items to send.");
            setSnackbarSeverity("warning");
            setSnackbarOpen(true);
            return;
        }
        const emailInfo = JSON.parse(sessionStorage.getItem("meeting_email_info") || "{}");
        if (!emailInfo.organizerEmail) {
            setSnackbarMessage("Set up email recipients first.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
            setOpenDialog(true);
            return;
        }
        setSendingMail(true);
        try {
            await axios.post(SCHEDULERX_SEND_MEETING_EMAIL_URL, {
                organizer_email: emailInfo.organizerEmail,
                cc_email: emailInfo.ccEmail || "",
                bcc_email: emailInfo.bccEmail || "",
                meeting_summary: meetingData.summary || transcription,
                action_items: approvedItems,
            });
            setSnackbarMessage("Mail sent successfully!");
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
        } catch (error) {
            const detail = error.response?.data?.detail;
            const isAuthError = error.response?.status === 401;
            setSnackbarMessage(detail || "Failed to send mail. Check SchedulerX email settings.");
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
        } finally {
            setSendingMail(false);
        }
    };

    return (
        <Box sx={{ marginTop: 4 }}>
            <Typography
                variant="h6"
                sx={{
                    color: "transparent",
                    background: "linear-gradient(90deg, rgb(237, 30, 121) 0%, rgb(0, 113, 188) 100%)",
                    backgroundClip: "text",
                    fontWeight: "bold",
                    marginBottom: 2,
                    textAlign: "left",
                }}
            >
                Action Items & Follow-up
            </Typography>

            <Typography variant="caption" sx={{ display: "block", color: "#94A3B8", marginBottom: 1, textAlign: "left" }}>
                Setup recipients below. Send Mail uses Azure Communication Services Email.
            </Typography>
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", marginBottom: 2 }}>
                <Button variant="contained" sx={{ bgcolor: "#7044ff" }} onClick={() => setOpenDialog(true)}>
                    Setup Email Recipients
                </Button>
                <Button
                    variant="contained"
                    sx={{ bgcolor: "#0a875a" }}
                    startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                    onClick={generateActionItems}
                    disabled={loading}
                >
                    Generate Action Items
                </Button>
            </Box>

            {meetingData?.summary && (
                <Box sx={{ padding: 2, background: "#1e293b", borderRadius: 2, marginBottom: 2, textAlign: "left" }}>
                    <Typography variant="subtitle2" sx={{ color: "#94A3B8" }}>Summary</Typography>
                    <Typography sx={{ color: "#e2e8f0" }}>{meetingData.summary}</Typography>
                </Box>
            )}

            {meetingData?.action_items?.length > 0 && (
                <Box sx={{ overflow: "auto", maxHeight: 420 }}>
                    {meetingData.action_items.map((item, index) => (
                        <Box
                            key={index}
                            sx={{
                                padding: 2,
                                background: "#e0e0e0",
                                borderRadius: 2,
                                marginBottom: 1,
                                textAlign: "left",
                                position: "relative",
                            }}
                        >
                            {statusMap[index] === "approved" && (
                                <CheckCircleIcon sx={{ color: "#43a047", position: "absolute", top: 8, right: 8 }} />
                            )}
                            {statusMap[index] === "rejected" && (
                                <CancelIcon sx={{ color: "#e53935", position: "absolute", top: 8, right: 8 }} />
                            )}
                            {editingIndex === index ? (
                                <>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Task"
                                        value={editData.task}
                                        onChange={(e) => setEditData({ ...editData, task: e.target.value })}
                                        sx={{ mb: 1 }}
                                    />
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Assigned To"
                                        value={editData.assigned_to}
                                        onChange={(e) => setEditData({ ...editData, assigned_to: e.target.value })}
                                        sx={{ mb: 1 }}
                                    />
                                    <TextField
                                        fullWidth
                                        size="small"
                                        label="Deadline"
                                        value={editData.deadline}
                                        onChange={(e) => setEditData({ ...editData, deadline: e.target.value })}
                                        sx={{ mb: 1 }}
                                    />
                                    <Box sx={{ display: "flex", gap: 1 }}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="success"
                                            onClick={() => {
                                                const updated = [...meetingData.action_items];
                                                updated[index] = editData;
                                                setMeetingData({ ...meetingData, action_items: updated });
                                                setEditingIndex(null);
                                            }}
                                        >
                                            Update
                                        </Button>
                                        <Button size="small" variant="contained" color="error" onClick={() => setEditingIndex(null)}>
                                            Cancel
                                        </Button>
                                    </Box>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body2"><strong>Task:</strong> {item.task}</Typography>
                                    <Typography variant="body2"><strong>Assigned To:</strong> {item.assigned_to}</Typography>
                                    <Typography variant="body2" sx={{ mb: 1 }}><strong>Deadline:</strong> {item.deadline}</Typography>
                                    {!statusMap[index] && (
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <Button
                                                size="small"
                                                sx={{ bgcolor: "#fdd835", color: "#000" }}
                                                onClick={() => {
                                                    setEditingIndex(index);
                                                    setEditData({ ...item });
                                                }}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="small"
                                                color="success"
                                                variant="contained"
                                                onClick={() => setStatusMap({ ...statusMap, [index]: "approved" })}
                                            >
                                                Approve
                                            </Button>
                                            <Button
                                                size="small"
                                                color="error"
                                                variant="contained"
                                                onClick={() => setStatusMap({ ...statusMap, [index]: "rejected" })}
                                            >
                                                Reject
                                            </Button>
                                        </Box>
                                    )}
                                </>
                            )}
                        </Box>
                    ))}
                    <Box sx={{ textAlign: "center", marginTop: 2 }}>
                        <Button
                            variant="contained"
                            sx={{ backgroundColor: "#1976d2" }}
                            disabled={
                                sendingMail ||
                                Object.keys(statusMap).length !== meetingData.action_items.length ||
                                Object.values(statusMap).some((v) => v !== "approved" && v !== "rejected")
                            }
                            onClick={handleSendMail}
                        >
                            {sendingMail ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : "Send Mail"}
                        </Button>
                    </Box>
                </Box>
            )}

            <Dialog open={openDialog} onClose={() => setOpenDialog(false)} fullWidth maxWidth="sm">
                <DialogTitle>Email Recipients</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                required
                                label="Organizer Email"
                                value={organizerEmail}
                                onChange={(e) => setOrganizerEmail(e.target.value)}
                                error={!!errors.organizerEmail}
                                helperText={errors.organizerEmail}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="CC (optional)" value={ccEmail} onChange={(e) => setCcEmail(e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="BCC (optional)" value={bccEmail} onChange={(e) => setBccEmail(e.target.value)} />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
                    <Button variant="contained" onClick={saveEmailSetup}>Save</Button>
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbarOpen} autoHideDuration={5000} onClose={() => setSnackbarOpen(false)}>
                <Alert severity={snackbarSeverity} onClose={() => setSnackbarOpen(false)}>
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default HealthActionItems;
